import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { CheckCircle, XCircle, Calendar, DollarSign, RefreshCw, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import AxiosDB from '@/config/axios2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionsList = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewalModal, setRenewalModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [renewalMonths, setRenewalMonths] = useState(1);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptions();
  }, []);



  const fetchSubscriptions = async () => {
    try {
      const response = await AxiosDB.get(`/subscriptions`);
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Error al cargar las suscripciones');
    } finally {
      setLoading(false);
    }
  };



  const handleCancel = async (id) => {
    try {
      await axios.patch(`${API}/subscriptions/${id}/cancel`);
      toast.success('Suscripción cancelada exitosamente');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Error al cancelar la suscripción');
    }
  };

  const handleRenewClick = (subscription) => {
    setSelectedSubscription(subscription);
    setRenewalMonths(1);
    setDiscountInfo(null);
    setRenewalModal(true);
  };

  const handleAddToCartRenewal = async () => {
    if (!selectedSubscription) return;

    setAddingToCart(true);
    try {
      await addToCart(selectedSubscription.product_id, 1, renewalMonths);
      toast.success(`Renovación agregada al carrito por ${renewalMonths} ${renewalMonths === 1 ? 'mes' : 'meses'}`);
      setShowConfirmation(true);
    } catch (error) {
      toast.error('Error al agregar al carrito');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleGoToCart = () => {
    setRenewalModal(false);
    setShowConfirmation(false);
    navigate('/dashboard/cart');
  };

  const handleContinueShopping = () => {
    setShowConfirmation(false);
    setRenewalModal(false);
  };

  const getDiscountLabel = (months) => {
    if (months >= 18) return '20% DESC';
    if (months >= 12) return '15% DESC';
    if (months >= 6) return '10% DESC';
    if (months >= 3) return '5% DESC';
    return '';
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">Cargando...</div>;
  }

  const activeSubscriptions = subscriptions.filter(s => s.estado === 'Activo');
  const inactiveSubscriptions = subscriptions.filter(s => s.estado !== 'Activo');

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Mis Suscripciones
        </h2>
        <p className="text-gray-600">Administra todas tus suscripciones activas e inactivas</p>
      </div>

      {/* Active Subscriptions */}
      {activeSubscriptions.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold mb-4 text-green-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Activas ({activeSubscriptions.length})
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activeSubscriptions.map((subscription) => (
              <Card key={subscription.id} className="shadow-lg border-l-4 border-l-green-500" data-testid={`subscription-${subscription.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {subscription.nombre}
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Activa
                        </Badge>
                      </CardTitle>
                      <CardDescription className="mt-2">
                        Suscripción de {subscription.duration_months || 1} {(subscription.duration_months || 1) === 1 ? 'mes' : 'meses'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="h-4 w-4" />
                      <span className="text-sm">Monto</span>
                    </div>
                    <div className="text-right">
                      {subscription.precio > subscription.renovacion ? (
                        <div>
                          <span className="text-sm line-through text-gray-400 mr-2">
                            ${subscription.precio}
                          </span>
                          <span className="text-lg font-bold text-green-600">
                            ${subscription.renovacion}
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-purple-600">
                          ${subscription.precio}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm">Próxima facturación</span>
                    </div>
                    <span className="font-semibold">
                      {new Date(subscription.vence).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                  {subscription.precio > subscription.renovacion > 0 && (
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg text-green-700">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        <span className="text-sm">Descuento aplicado</span>
                      </div>
                      <span className="font-semibold">
                        ${subscription.precio - subscription.renovacion}
                      </span>
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    Inicio: {new Date(subscription.adquirido).toLocaleDateString('es-ES')}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      onClick={() => handleRenewClick(subscription)}
                      data-testid={`renew-subscription-${subscription.id}`}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Renovar
                    </Button>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          className="flex-1"
                          data-testid={`cancel-subscription-${subscription.id}`}
                        >
                          Cancelar
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción cancelará tu suscripción a {subscription.product_name}.
                            Perderás el acceso al finalizar el período actual.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Volver</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleCancel(subscription.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Confirmar Cancelación
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Inactive Subscriptions */}
      {inactiveSubscriptions.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold mb-4 text-gray-500" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Inactivas ({inactiveSubscriptions.length})
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {inactiveSubscriptions.map((subscription) => (
              <Card key={subscription.id} className="shadow-lg border-l-4 border-l-gray-400 opacity-75" data-testid={`subscription-${subscription.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {subscription.nombre}
                        <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                          <XCircle className="h-3 w-3 mr-1" />
                          {subscription.estado}
                        </Badge>
                      </CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Monto</span>
                    <span className="font-semibold">${subscription.renovacion}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Período</span>
                    <span className="font-semibold">
                      {new Date(subscription.adquirido).toLocaleDateString('es-ES')} -
                      {new Date(subscription.vence).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                  <Button
                    className="w-full mt-4"
                    onClick={() => handleRenewClick(subscription)}
                    data-testid={`renew-subscription-${subscription.id}`}
                    variant="outline"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reactivar Suscripción
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {subscriptions.length === 0 && (
        <Card className="shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <p className="text-xl font-semibold text-gray-500">No tienes suscripciones aún</p>
            <p className="text-gray-400 mt-2">Explora nuestros productos para comenzar</p>
          </CardContent>
        </Card>
      )}

      {/* Renewal Modal */}
      <Dialog open={renewalModal} onOpenChange={setRenewalModal}>
        <DialogContent className="max-w-lg">
          {!showConfirmation ? (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Renovar Suscripción
                </DialogTitle>
                <DialogDescription>
                  Selecciona la cantidad de meses para renovar tu suscripción a{' '}
                  <strong>{selectedSubscription?.product_name}</strong>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Duration Selector */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Duración de renovación:</label>
                  <Select
                    value={renewalMonths.toString()}
                    onValueChange={(value) => setRenewalMonths(parseInt(value))}
                  >
                    <SelectTrigger data-testid="renewal-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(24)].map((_, i) => {
                        const months = i + 1;
                        const label = getDiscountLabel(months);
                        return (
                          <SelectItem key={months} value={months.toString()}>
                            {months} {months === 1 ? 'mes' : 'meses'} {label && `(${label})`}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Discount Info */}
                {discountInfo && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Precio base/mes:</span>
                      <span className="font-medium">${discountInfo.base_price}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Meses:</span>
                      <span className="font-medium">{discountInfo.months}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">${discountInfo.original_total}</span>
                    </div>
                    {discountInfo.discount_percentage > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-green-600">
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-4 w-4" />
                            Descuento ({discountInfo.discount_percentage}%):
                          </span>
                          <span className="font-medium">-${discountInfo.discount_amount}</span>
                        </div>
                        <div className="border-t pt-3 flex justify-between">
                          <span className="font-semibold">Total a pagar:</span>
                          <span className="text-2xl font-bold text-green-600">
                            ${discountInfo.final_total}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 text-center">
                          ¡Ahorras ${discountInfo.discount_amount} renovando por {renewalMonths} meses!
                        </div>
                      </>
                    )}
                    {discountInfo.discount_percentage === 0 && (
                      <div className="border-t pt-3 flex justify-between">
                        <span className="font-semibold">Total a pagar:</span>
                        <span className="text-2xl font-bold text-purple-600">
                          ${discountInfo.final_total}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Info message */}
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-blue-700 text-sm">
                  <Calendar className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Al agregar al carrito, podrás revisar los detalles y proceder con el pago cuando estés listo.
                  </span>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setRenewalModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddToCartRenewal}
                  disabled={addingToCart}
                  data-testid="confirm-renewal-button"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}
                >
                  {addingToCart ? 'Agregando...' : 'Agregar al Carrito'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-green-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  <CheckCircle className="h-6 w-6" />
                  ¡Producto Agregado!
                </DialogTitle>
                <DialogDescription>
                  La renovación de <strong>{selectedSubscription?.product_name}</strong> por{' '}
                  <strong>{renewalMonths} {renewalMonths === 1 ? 'mes' : 'meses'}</strong> se agregó exitosamente al carrito.
                </DialogDescription>
              </DialogHeader>

              <div className="py-6">
                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg text-center">
                  <p className="text-lg font-semibold mb-2">¿Qué deseas hacer ahora?</p>
                  <p className="text-sm text-gray-600">
                    Puedes ir al carrito para completar tu compra o seguir agregando más productos.
                  </p>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={handleContinueShopping}
                  data-testid="continue-shopping-button"
                  className="w-full sm:w-auto"
                >
                  Seguir Agregando
                </Button>
                <Button
                  onClick={handleGoToCart}
                  data-testid="go-to-cart-button"
                  className="w-full sm:w-auto"
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}
                >
                  Ir al Carrito
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionsList;
